// ABOUTME: Generic hook for fetching and managing user parameter history
// ABOUTME: Provides history data, delete mutation, and loading states for any parameter type

import { useQuery, useMutation } from "@apollo/client/react"
import { useCallback, useMemo } from "react"
import {
  UserParameterHistoryDocument,
  DeleteUserParameterHistoryItemDocument,
  type UserParameterName,
} from "@/graphql/main/gql/graphql"
import { evictHistoryRow } from "./evictHistoryRow"
// By module, not through the ui barrel: the barrel pulls the side page, which pulls this
// hook back through @shared/hooks — five of the project's thirteen cycles started here.
import type { HistoryDataPoint } from "@shared/ui/ParameterSidePage/ParameterHistoryChart"

interface UseParameterHistoryProps {
  /** Parameter name to fetch history for */
  parameterName: UserParameterName
  /** User ID */
  userID: string | undefined
  /** Whether to skip the query */
  skip?: boolean
}

interface UseParameterHistoryReturn {
  /** History data points */
  history: HistoryDataPoint[]
  /** Whether history is loading */
  loading: boolean
  /** Whether delete is in progress */
  deleting: boolean
  /** Refetch history data */
  refetch: () => void
  /** Delete a history item by date */
  deleteItem: (date: string) => Promise<void>
}

export function useParameterHistory({
  parameterName,
  userID,
  skip = false,
}: UseParameterHistoryProps): UseParameterHistoryReturn {
  const { data, loading, refetch } = useQuery(UserParameterHistoryDocument, {
    variables: {
      parameterName,
      userID,
      from: null,
      to: null,
    },
    skip: skip || !userID,
    fetchPolicy: "cache-and-network",
  })

  const [deleteMutation, { loading: deleting }] = useMutation(DeleteUserParameterHistoryItemDocument)

  const history = useMemo<HistoryDataPoint[]>(() => {
    if (!data?.userParameterHistory) return []
    return data.userParameterHistory.map((item) => {
      // Handle nested value structure: item.value can be { date, method, value } or a scalar
      const rawValue = item.value
      let numericValue = 0
      if (typeof rawValue === "number") {
        numericValue = rawValue
      } else if (typeof rawValue === "object" && "value" in rawValue) {
        numericValue = Number(rawValue.value) || 0
      } else {
        numericValue = Number(rawValue) || 0
      }
      return {
        date: item.date,
        value: numericValue,
      }
    })
  }, [data])

  const deleteItem = useCallback(
    async (date: string) => {
      // Evict on success so the list updates from the mutation alone; the
      // background refetch is best-effort and can't resurrect the row or raise
      // a false error (deleteItem rejects only if the server delete failed).
      await deleteMutation({
        variables: {
          date,
          parameterName,
          userID,
        },
        update: (cache) =>
          evictHistoryRow(cache, {
            document: UserParameterHistoryDocument,
            variables: { parameterName, userID, from: null, to: null },
            responseKey: "userParameterHistory",
            date,
          }),
      })
      void refetch().catch(() => {})
    },
    [deleteMutation, parameterName, userID, refetch],
  )

  const handleRefetch = useCallback(() => {
    void refetch()
  }, [refetch])

  return {
    history,
    loading,
    deleting,
    refetch: handleRefetch,
    deleteItem,
  }
}
